import {
  LayoutDashboard,
  Users,
  UserPlus,
  MessageSquare,
  Calendar,
  HelpCircle,
  BarChart3,
  Settings,
  CreditCard,
  FileText,
  TrendingUp,
  ShoppingCart,
  Package,
  BookOpen,
  Shield,
  Home,
  Mail,
  Bell,
  Search,
  Database,
  Key,
  Globe,
  Monitor,
  Smartphone,
  Cloud,
  Zap,
  Wand2,
  Megaphone,
  Folder,
  Wifi,
  FileCheck2,
  Network,
  Server,
  HardDrive,
  Upload,
  Download,
  Activity,
  EyeOff,
  Terminal,
} from "lucide-react";
import { LucideIcon } from "lucide-react";

export interface MenuItem {
  id: string;
  title: string;
  icon: LucideIcon;
  href?: string;
  badge?: string | number;
  children?: MenuItem[];
  // 🎯 스타터킷 확장 기능
  permissions?: string[];
  enabled?: boolean;
  template?: string;
  description?: string;
  isNew?: boolean;
  isBeta?: boolean;
}

// 🏗️ 메뉴 템플릿 타입
export type MenuTemplate = 'crm' | 'ecommerce' | 'blog' | 'admin' | 'saas' | 'custom';

// 🎯 Incident Control Shared Block (에러 대시보드)
const incidentControlItem: MenuItem = {
  id: "incident-control",
  title: "장애 관제",
  icon: Activity,
  children: [
    {
      id: "log-dashboard",
      title: "에러 대시보드",
      icon: LayoutDashboard,
      href: "/log-dashboard",
      description: "실시간 ODT 에러 현황 모니터링",
    },
    {
      id: "log-search",
      title: "에러 상세 검색",
      icon: Search,
      href: "/log-dashboard/search",
      description: "조건별 에러 로그 상세 조회",
    },
    {
      id: "log-ignore-settings",
      title: "에러 무시 설정",
      icon: EyeOff,
      href: "/log-dashboard/ignore-settings",
      description: "대시보드 제외 에러 패턴 관리",
    },
  ],
};

// 🎯 Admin 메뉴 템플릿 (기본)
export const adminMenuItems: MenuItem[] = [
  {
    id: "dashboard",
    title: "대시보드",
    icon: LayoutDashboard,
    href: "/dashboard",
    description: "시스템 전체 현황을 확인하세요",
  },
  incidentControlItem,
  {
    id: "users",
    title: "사용자 관리",
    icon: Users,
    children: [
      {
        id: "user-list",
        title: "사용자 목록",
        icon: Users,
        href: "/users",
        description: "등록된 사용자들을 관리하세요",
      },
      {
        id: "user-roles",
        title: "권한 관리",
        icon: Shield,
        href: "/users/roles",
        description: "사용자 권한 및 역할을 설정하세요",
      },
      {
        id: "user-add",
        title: "사용자 추가",
        icon: UserPlus,
        href: "/users/add",
        description: "새로운 사용자를 등록하세요",
      },
    ],
  },
  {
    id: "content",
    title: "콘텐츠 관리",
    icon: FileText,
    children: [
      {
        id: "pages",
        title: "페이지 관리",
        icon: FileText,
        href: "/content/pages",
      },
      {
        id: "media",
        title: "미디어 라이브러리",
        icon: Package,
        href: "/content/media",
      },
    ],
  },
  {
    id: "analytics",
    title: "분석 & 통계",
    icon: BarChart3,
    children: [
      {
        id: "overview",
        title: "전체 현황",
        icon: TrendingUp,
        href: "/analytics/overview",
      },
      {
        id: "traffic",
        title: "트래픽 분석",
        icon: Globe,
        href: "/analytics/traffic",
      },
      {
        id: "performance",
        title: "성능 모니터링",
        icon: Zap,
        href: "/analytics/performance",
        isNew: true,
      },
    ],
  },
  {
    id: "system",
    title: "시스템 관리",
    icon: Settings,
    children: [
      {
        id: "settings",
        title: "일반 설정",
        icon: Settings,
        href: "/system/settings",
      },
      {
        id: "database",
        title: "데이터베이스",
        icon: Database,
        href: "/system/database",
      },
      {
        id: "security",
        title: "보안 설정",
        icon: Key,
        href: "/system/security",
      },
      {
        id: "backup",
        title: "백업 관리",
        icon: Cloud,
        href: "/system/backup",
      },
    ],
  },
  {
    id: "notifications",
    title: "알림 센터",
    icon: Bell,
    href: "/notifications",
    badge: 3,
  },
  {
    id: "help",
    title: "도움말",
    icon: HelpCircle,
    href: "/help",
  },
];

// 🎯 CRM 메뉴 템플릿 - WJPNCMA 맞춤형
export const crmMenuItems: MenuItem[] = [
  {
    id: "dashboard",
    title: "대시보드",
    icon: LayoutDashboard,
    href: "/dashboard",
    description: "전체 현황 및 ODT 상태를 모니터링하세요",
  },
  incidentControlItem,
  {
    id: "gas-stations",
    title: "주유소 관리",
    icon: Home,
    children: [
      {
        id: "station-list",
        title: "주유소 목록",
        icon: Home,
        href: "/gas-stations",
        description: "등록된 주유소 목록을 관리하세요",
      },
      {
        id: "connect-service",
        title: "커넥트서비스",
        icon: Network,
        href: "/gas-stations/connect-service",
        description: "원격 PC 연결 및 파일 관리 서비스",
        isNew: true,
      },
      {
        id: "connect-service2",
        title: "커넥트서비스2",
        icon: Network,
        href: "/gas-stations/connect-service2",
        description: "원격 PC 연결 및 파일 관리 서비스 v2",
        isNew: true,
      },
      // {
      //   id: "config-monitoring",
      //   title: "실시간 모니터링",
      //   icon: Zap,
      //   href: "/odt-config/monitoring",
      //   description: "현장 ODT 상태 실시간 감시",
      // },
    ],
  },
  {
    id: "odt-config",
    title: "ODT 설정 관리",
    icon: Settings,
    children: [
      {
        id: "config-search",
        title: "ODTConfig 내용 검색",
        icon: Search,
        href: "/odt-config/search",
        description: "XML 요소와 속성을 검색하고 관리",
      },
      {
        id: "station-search",
        title: "주유소별 설정내용 검색",
        icon: Search,
        href: "/odt-config/station-search",
        description: "주유소별 설정내용 검색",
      },
      {
        id: "xml-validator",
        title: "XML 유효성 검증",
        icon: FileCheck2,
        href: "/odt-config/xml-validator",
        description: "XML 문서의 유효성을 검증합니다",
        isNew: true,
      },
      {
        id: "config-generator",
        title: "ODTConfig 자동 생성",
        icon: Wand2,
        href: "/odt-config/generator",
        description: "템플릿 기반 설정 파일 자동 생성",
      },
      {
        id: "config-editor",
        title: "원격 설정 편집",
        icon: Monitor,
        href: "/odt-config/editor",
        description: "실시간 원격 설정 편집기",
      },
      {
        id: "command-management",
        title: "원격 명령 관리",
        icon: Terminal,
        href: "/system/commands",
        description: "장비별 환경설정 및 명령 배포 관리",
        isNew: true,
      },
    ],
  },
  {
    id: "support",
    title: "고객 지원",
    icon: MessageSquare,
    children: [
      {
        id: "support-tickets",
        title: "응대 이력",
        icon: MessageSquare,
        href: "/support/tickets",
        description: "고객 문의 및 처리 이력",
      },
      {
        id: "support-tickets-manage",
        title: "티켓 관리",
        icon: Calendar,
        href: "/support/manage",
        description: "티켓 상태 및 담당자 관리",
      },
      {
        id: "support-faq",
        title: "FAQ 관리",
        icon: HelpCircle,
        href: "/support/faq",
        description: "자주 묻는 질문 관리",
      },
      {
        id: "support-satisfaction",
        title: "만족도 조사",
        icon: TrendingUp,
        href: "/support/satisfaction",
        description: "고객 만족도 설문 및 분석",
      },
    ],
  },
  {
    id: "board",
    title: "게시판",
    icon: Megaphone,
    children: [
      {
        id: "board-home",
        title: "전체 게시판",
        icon: Folder,
        href: "/board",
        description: "모든 게시판을 한눈에 확인하세요",
      },
      {
        id: "board-notice",
        title: "공지사항",
        icon: Megaphone,
        href: "/board/notice",
        description: "시스템 공지사항 및 중요 안내",
      },
      {
        id: "board-faq",
        title: "FAQ",
        icon: HelpCircle,
        href: "/board/faq",
        description: "자주 묻는 질문과 답변",
      },
      {
        id: "board-qna",
        title: "Q&A",
        icon: MessageSquare,
        href: "/board/qna",
        description: "질문과 답변 게시판",
      },
      {
        id: "board-resources",
        title: "자료실",
        icon: BookOpen,
        href: "/board/resources",
        description: "각종 자료 및 문서 다운로드",
      },
      {
        id: "board-support",
        title: "기술지원",
        icon: Settings,
        href: "/board/support",
        description: "기술 지원 및 문제 해결",
      },
      {
        id: "board-ssdreplace",
        title: "SSD교체이력",
        icon: Settings,
        href: "/board/ssdreplace",
        description: "SSD 교체 이력",
      },
    ],
  },
  {
    id: "service-management",
    title: "설치/A/S",
    icon: Monitor,
    children: [
      {
        id: "installation",
        title: "설치 관리",
        icon: Package,
        href: "/service/installation",
        description: "서비스 설치 계획 및 진행 현황",
      },
      {
        id: "as-history",
        title: "A/S 이력",
        icon: Settings,
        href: "/service/as-history",
        description: "방문 A/S 기록 및 관리",
      },
      {
        id: "engineers",
        title: "엔지니어 관리",
        icon: Users,
        href: "/service/engineers",
        description: "기술자 배정 및 일정 관리",
      },
      {
        id: "schedule",
        title: "일정 관리",
        icon: Calendar,
        href: "/service/schedule",
        description: "설치 및 A/S 일정 캘린더",
      },
    ],
  },
  {
    id: "reports",
    title: "리포트",
    icon: BarChart3,
    children: [
      {
        id: "operations",
        title: "운영 통계",
        icon: TrendingUp,
        href: "/reports/operations",
        description: "전체 운영 현황 통계",
      },
      {
        id: "performance",
        title: "성과 분석",
        icon: BarChart3,
        href: "/reports/performance",
        description: "서비스 성과 및 KPI 분석",
      },
      {
        id: "quality",
        title: "품질 리포트",
        icon: Shield,
        href: "/reports/quality",
        description: "서비스 품질 및 만족도 리포트",
      },
      {
        id: "user-activity",
        title: "사용자 활동",
        icon: Users,
        href: "/reports/user-activity",
        description: "시스템 사용자 활동 로그",
      },
    ],
  },
  {
    id: "system",
    title: "시스템 관리",
    icon: Settings,
    children: [
      {
        id: "user-management",
        title: "사용자 관리",
        icon: Users,
        href: "/system/users",
        description: "직원 계정 및 권한 관리",
      },
      {
        id: "code-management",
        title: "코드 관리",
        icon: Database,
        href: "/system/codes",
        description: "공통 코드 및 설정값 관리",
      },
      {
        id: "logs",
        title: "로그 관리",
        icon: FileText,
        href: "/system/logs",
        description: "시스템 로그 및 감사 추적",
      },
    ],
  },
  {
    id: "notifications",
    title: "알림 센터",
    icon: Bell,
    href: "/notifications",
    badge: 5,
    description: "실시간 알림 및 중요 메시지",
  },
];

// 🛒 E-commerce 메뉴 템플릿
export const ecommerceMenuItems: MenuItem[] = [
  {
    id: "dashboard",
    title: "대시보드",
    icon: LayoutDashboard,
    href: "/dashboard",
  },
  {
    id: "products",
    title: "상품관리",
    icon: Package,
    children: [
      {
        id: "product-list",
        title: "상품목록",
        icon: Package,
        href: "/products",
      },
      {
        id: "product-add",
        title: "상품등록",
        icon: UserPlus,
        href: "/products/add",
      },
      {
        id: "categories",
        title: "카테고리",
        icon: FileText,
        href: "/products/categories",
      },
      {
        id: "inventory",
        title: "재고관리",
        icon: Database,
        href: "/products/inventory",
        isNew: true,
      },
    ],
  },
  {
    id: "orders",
    title: "주문관리",
    icon: ShoppingCart,
    children: [
      {
        id: "order-list",
        title: "주문목록",
        icon: ShoppingCart,
        href: "/orders",
      },
      {
        id: "order-status",
        title: "주문상태",
        icon: TrendingUp,
        href: "/orders/status",
      },
      {
        id: "shipping",
        title: "배송관리",
        icon: Package,
        href: "/orders/shipping",
      },
    ],
  },
  {
    id: "customers",
    title: "고객관리",
    icon: Users,
    href: "/customers",
  },
  {
    id: "marketing",
    title: "마케팅",
    icon: Mail,
    children: [
      {
        id: "campaigns",
        title: "캠페인",
        icon: Mail,
        href: "/marketing/campaigns",
      },
      {
        id: "coupons",
        title: "쿠폰/할인",
        icon: CreditCard,
        href: "/marketing/coupons",
      },
    ],
  },
  {
    id: "analytics",
    title: "통계",
    icon: BarChart3,
    href: "/analytics",
  },
  {
    id: "settings",
    title: "설정",
    icon: Settings,
    href: "/settings",
  },
];

// 📝 Blog 메뉴 템플릿
export const blogMenuItems: MenuItem[] = [
  {
    id: "dashboard",
    title: "대시보드",
    icon: LayoutDashboard,
    href: "/dashboard",
  },
  {
    id: "posts",
    title: "포스트",
    icon: BookOpen,
    children: [
      {
        id: "post-list",
        title: "포스트 목록",
        icon: BookOpen,
        href: "/posts",
      },
      {
        id: "post-write",
        title: "포스트 작성",
        icon: UserPlus,
        href: "/posts/write",
      },
      {
        id: "drafts",
        title: "임시저장",
        icon: FileText,
        href: "/posts/drafts",
        badge: 5,
      },
    ],
  },
  {
    id: "categories",
    title: "카테고리",
    icon: FileText,
    href: "/categories",
  },
  {
    id: "comments",
    title: "댓글관리",
    icon: MessageSquare,
    href: "/comments",
    badge: "new",
  },
  {
    id: "media",
    title: "미디어",
    icon: Package,
    href: "/media",
  },
  {
    id: "seo",
    title: "SEO 관리",
    icon: Search,
    href: "/seo",
    isBeta: true,
  },
  {
    id: "analytics",
    title: "통계",
    icon: BarChart3,
    href: "/analytics",
  },
  {
    id: "settings",
    title: "설정",
    icon: Settings,
    href: "/settings",
  },
];

// 🚀 SaaS 메뉴 템플릿
export const saasMenuItems: MenuItem[] = [
  {
    id: "dashboard",
    title: "대시보드",
    icon: LayoutDashboard,
    href: "/dashboard",
  },
  {
    id: "workspace",
    title: "워크스페이스",
    icon: Monitor,
    children: [
      {
        id: "projects",
        title: "프로젝트",
        icon: FileText,
        href: "/workspace/projects",
      },
      {
        id: "team",
        title: "팀 관리",
        icon: Users,
        href: "/workspace/team",
      },
      {
        id: "integrations",
        title: "연동 서비스",
        icon: Zap,
        href: "/workspace/integrations",
      },
    ],
  },
  {
    id: "billing",
    title: "결제/구독",
    icon: CreditCard,
    children: [
      {
        id: "subscription",
        title: "구독 관리",
        icon: CreditCard,
        href: "/billing/subscription",
      },
      {
        id: "invoices",
        title: "청구서",
        icon: FileText,
        href: "/billing/invoices",
      },
      {
        id: "usage",
        title: "사용량",
        icon: BarChart3,
        href: "/billing/usage",
      },
    ],
  },
  {
    id: "api",
    title: "API & 개발",
    icon: Globe,
    children: [
      {
        id: "api-keys",
        title: "API 키",
        icon: Key,
        href: "/api/keys",
      },
      {
        id: "webhooks",
        title: "웹훅",
        icon: Zap,
        href: "/api/webhooks",
      },
      {
        id: "docs",
        title: "API 문서",
        icon: BookOpen,
        href: "/api/docs",
      },
    ],
  },
  {
    id: "analytics",
    title: "분석",
    icon: BarChart3,
    href: "/analytics",
  },
  {
    id: "settings",
    title: "설정",
    icon: Settings,
    href: "/settings",
  },
];

// 🚀 메뉴 템플릿 매핑
export const menuTemplates: Record<MenuTemplate, MenuItem[]> = {
  admin: adminMenuItems,
  crm: crmMenuItems,
  ecommerce: ecommerceMenuItems,
  blog: blogMenuItems,
  saas: saasMenuItems,
  custom: adminMenuItems, // 기본값
};

// 🔧 메뉴 유틸리티 함수들

// 메뉴 템플릿 선택
export function getMenuByTemplate(template: MenuTemplate): MenuItem[] {
  return menuTemplates[template] || adminMenuItems;
}

// 권한 기반 메뉴 필터링
export function filterMenuByPermissions(
  menu: MenuItem[], 
  userPermissions: string[]
): MenuItem[] {
  return menu.filter(item => {
    if (item.permissions && item.permissions.length > 0) {
      return item.permissions.some(permission => userPermissions.includes(permission));
    }
    return true;
  }).map(item => ({
    ...item,
    children: item.children ? filterMenuByPermissions(item.children, userPermissions) : undefined
  }));
}

// 활성화된 메뉴만 필터링
export function filterEnabledMenuItems(menu: MenuItem[]): MenuItem[] {
  return menu.filter(item => item.enabled !== false).map(item => ({
    ...item,
    children: item.children ? filterEnabledMenuItems(item.children) : undefined
  }));
}

// 메뉴 검색
export function searchMenuItems(menu: MenuItem[], query: string): MenuItem[] {
  const lowerQuery = query.toLowerCase();
  
  return menu.filter(item => {
    // 제목이나 설명에서 검색
    const titleMatch = item.title.toLowerCase().includes(lowerQuery);
    const descMatch = item.description?.toLowerCase().includes(lowerQuery);
    
    // 자식 메뉴에서도 검색
    const childMatch = item.children?.some(child => 
      child.title.toLowerCase().includes(lowerQuery) ||
      child.description?.toLowerCase().includes(lowerQuery)
    );
    
    return titleMatch || descMatch || childMatch;
  });
}

// 메뉴 깊이 계산
export function getMenuDepth(menu: MenuItem[]): number {
  let maxDepth = 0;
  
  function traverse(items: MenuItem[], depth: number) {
    maxDepth = Math.max(maxDepth, depth);
    items.forEach(item => {
      if (item.children) {
        traverse(item.children, depth + 1);
      }
    });
  }
  
  traverse(menu, 1);
  return maxDepth;
}